// Copyright (c) 2022 by Move Closer

import { ModalSize } from '../../../../backoffice/shared/components'
import { AllowedAttributes } from '../../../../contexts'
import { ShapeMap, SizeMap, VariantMap } from '../../../../dsl/composables'
import { BadgeProps } from '../../../../dsl/atoms/Badge'

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const PRODUCT_HEADER_COMPONENT_KEY = 'ProductHeaderComponent'

export type ProductBadgeProps = BadgeProps & {
  icon?: string
}

export type ProductBadgeConfig = Omit<ProductBadgeProps, 'size'> & {
  size: SizeMap | {
    mobile: SizeMap
    desktop: SizeMap
  }
}

export type ProductHeaderBadgeRegistry = Partial<Record<AllowedAttributes, ProductBadgeConfig>> & { default: ProductBadgeConfig }

export interface ProductHeaderConfig {
  badgeRegistry: ProductHeaderBadgeRegistry
  badgesOnGallery: boolean
  defaultMaxRating: number
  descriptionAttribute?: AllowedAttributes
  displayMainCategoryLogo: boolean
  doubleCheckStockStatus: boolean
  favouriteAsIcon: boolean
  hasBenefitsBar: boolean
  hasDeliveryTimer: boolean
  hasGiftsBox: boolean
  hasMilesAndMore: boolean
  hasQuantity: boolean
  hasRating: boolean
  hasPriceInAddToCart: boolean
  hasPriceNameBar: boolean
  highlightedAttributes: AllowedAttributes[] | null
  icons: Record<string, string>
  isVariantNameEnabled: boolean
  listedAttributes: AllowedAttributes[] | null
  modalSize: ModalSize
  orderDetails?: Record<string, string> // todo: aelia {delivery: 'Truck', payments: 'CreditCard', payPro: 'Squares'}
  promotionBadgeHasLabel: boolean
  shopOnlyAttributes: AllowedAttributes[] | null
  shouldDisplayDiscount: boolean
  shouldDisplayNetto: boolean
  shouldDisplayOmnibus: boolean
  shouldDisplayRegularPriceForDiscount: boolean
  shouldDisplayReviewWithDescription: boolean
  shouldDisplaySimplePrice: boolean
  shouldDisplayUnits: boolean
  shouldHaveSeparatedRating: boolean
  shouldHaveVisibleRating: boolean
  showSingleVariantRating: boolean
  useDrawer: boolean
  useVendorReviews: boolean
  variantSwitcherShowChosen: boolean
}

export const defaultBadgeRegistry: ProductHeaderBadgeRegistry = {
  default: {
    theme: 'default',
    shape: ShapeMap.Rectangle,
    variant: VariantMap.Full,
    size: SizeMap.Medium
  },
  [AllowedAttributes.IsNew]: {
    theme: 'default',
    shape: ShapeMap.Rectangle, // todo: aelia ShapeMap.Square
    variant: VariantMap.Full,
    size: SizeMap.Medium // todo: aelia { mobile: SizeMap.Small, desktop: SizeMap.Large }
  },
  [AllowedAttributes.IsNatural]: {
    icon: 'Leaf',
    theme: 'success',
    shape: ShapeMap.Square,
    variant: VariantMap.Outline,
    size: { mobile: SizeMap.Small, desktop: SizeMap.Large }
  },
  [AllowedAttributes.IsSale]: {
    theme: 'primary',
    shape: ShapeMap.Rectangle,
    variant: VariantMap.Full,
    size: SizeMap.Medium
  },
  [AllowedAttributes.IsFaF]: {
    theme: 'premium',
    shape: ShapeMap.Square,
    variant: VariantMap.Full,
    size: { mobile: SizeMap.Small, desktop: SizeMap.Large }
  },
  [AllowedAttributes.IsPresale]: {
    theme: 'warning',
    shape: ShapeMap.Square,
    variant: VariantMap.Full,
    size: { mobile: SizeMap.Small, desktop: SizeMap.Large }
  },
  [AllowedAttributes.HasFreeDelivery]: {
    icon: 'FreeDelivery',
    theme: '',
    shape: ShapeMap.Square,
    variant: VariantMap.Outline,
    size: { mobile: SizeMap.Small, desktop: SizeMap.Large }
  }
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const PRODUCT_HEADER_DEFAULT_CONFIG: ProductHeaderConfig = {
  badgeRegistry: defaultBadgeRegistry,
  badgesOnGallery: false,
  displayMainCategoryLogo: false,
  doubleCheckStockStatus: false,
  favouriteAsIcon: false,
  defaultMaxRating: 5,
  highlightedAttributes: null,
  listedAttributes: null,
  hasBenefitsBar: true, // todo: aelia false
  hasDeliveryTimer: false,
  hasGiftsBox: false,
  hasMilesAndMore: false, // todo: aelia true
  hasQuantity: false,
  hasPriceInAddToCart: true,
  hasPriceNameBar: false,
  hasRating: true,
  icons: {
    addToCartButtonIcon: '',
    heartEmptyIcon: 'HeartEmptyIcon',
    heartFullIcon: 'HeartFullIcon',
    infoIcon: 'InfoIcon',
    notifyButtonIcon: ''
  },
  promotionBadgeHasLabel: false,
  variantSwitcherShowChosen: false,
  modalSize: ModalSize.md,
  useDrawer: false,
  useVendorReviews: false,
  shopOnlyAttributes: null,
  shouldDisplayNetto: false,
  shouldHaveSeparatedRating: false,
  shouldHaveVisibleRating: true,
  showSingleVariantRating: false,
  isVariantNameEnabled: false,
  shouldDisplayOmnibus: true,
  shouldDisplayReviewWithDescription: true,
  shouldDisplayDiscount: false,
  shouldDisplayRegularPriceForDiscount: false,
  shouldDisplaySimplePrice: true,
  shouldDisplayUnits: true
}

/**
 * @author Maciej Perzankowski <maciej.perzankowski@movecloser.pl>
 */
export const PRODUCT_HEADER_GALLERY_THUMBNAILS_SWIPER_CONFIG = {
  slidesPerView: 5,
  spaceBetween: 10
}
